<template>
  <!-- Page Header -->
  <PageHeader 
    title="Liste des Lookbooks" 
    subtitle="Gérez vos lookbooks et collections" 
    class="mb-6 pr-10" 
  />
  
  <div class="w-full px-6 py-8">
    <!-- Bouton Ajouter un lookbook -->
    <div class="flex justify-start mb-6">
      <button 
        @click="showFormSection"
        class="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors shadow-sm"
      >
        <i class="fas fa-plus mr-2"></i>
        Ajouter un lookbook
      </button>
    </div>

    <!-- Section Liste des Lookbooks -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-2xl font-bold text-gray-900">Liste des Lookbooks</h2>
        <div class="text-sm text-gray-500">
          {{ lookbooks.length }} lookbook{{ lookbooks.length > 1 ? 's' : '' }}
        </div>
      </div>
      
      <!-- Indicateur de chargement -->
      <div v-if="isLoading" class="text-center py-12">
        <i class="fas fa-spinner fa-spin text-gray-400 text-4xl mb-4"></i>
        <div class="text-gray-600">Chargement des lookbooks...</div>
      </div>
      
      <!-- Message si aucun lookbook -->
      <div v-else-if="lookbooks.length === 0" class="text-center py-12">
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-6 inline-block">
          <i class="fas fa-book-open text-blue-400 text-4xl mb-4"></i>
          <div class="text-blue-800 font-medium mb-2">Aucun lookbook</div>
          <div class="text-blue-600 text-sm">
            Vous n'avez aucun lookbook sur Efashion Paris. Créez votre premier lookbook !
          </div>
        </div>
      </div>
      
      <!-- Grille des lookbooks -->
      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div 
          v-for="lookbook in lookbooks" 
          :key="lookbook.id"
          @click="handleEdit(lookbook)"
          class="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden hover:shadow-md hover:border-indigo-300 transition-all cursor-pointer"
        >
          <!-- Image de couverture -->
          <div class="aspect-video bg-gray-200 flex items-center justify-center relative">
            <div v-if="!lookbook.banniere" class="text-center">
              <i class="fas fa-image text-gray-400 text-3xl mb-2"></i>
              <div class="text-gray-500 text-sm">Aucune couverture</div>
            </div>
            <img 
              v-else 
              :src="lookbook.banniere" 
              :alt="lookbook.titre.francais"
              class="w-full h-full object-cover"
            />
            
            <!-- Badge de statut -->
            <div class="absolute top-2 right-2">
              <span 
                :class="[
                  'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium',
                  lookbook.actif 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-800'
                ]"
              >
                <i :class="lookbook.actif ? 'fas fa-check-circle' : 'fas fa-pause-circle'" class="mr-1"></i>
                {{ lookbook.actif ? 'Actif' : 'Inactif' }}
              </span>
            </div>
          </div>
          
          <!-- Informations du lookbook -->
          <div class="p-4">
            <h3 class="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
              {{ lookbook.titre.francais }}
            </h3>
            <p class="text-sm text-gray-600 mb-3">
              <i class="fas fa-calendar mr-1"></i>
              {{ lookbook.annee }} - Collection {{ lookbook.collection }}
            </p>
            
            <!-- Catégories -->
            <div class="flex flex-wrap gap-1 mb-4">
              <span 
                v-if="lookbook.categories.length > 0"
                class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
              >
                <i class="fas fa-tag mr-1"></i>
                {{ lookbook.categories.length }} catégorie{{ lookbook.categories.length > 1 ? 's' : '' }}
              </span>
            </div>
            
            <!-- Date de création -->
            <div class="text-xs text-gray-500 mb-4">
              <i class="fas fa-clock mr-1"></i>
              Créé le {{ formatDate(lookbook.dateCreation) }}
            </div>
            
            <!-- Actions -->
            <div class="flex items-center gap-2">
              <!-- Bouton Éditer -->
              <button 
                @click.stop="handleEdit(lookbook)"
                class="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                title="Éditer"
              >
                <i class="fas fa-edit mr-1"></i>
                Éditer
              </button>
              
              <!-- Bouton Activer/Désactiver -->
              <button 
                @click.stop="handleToggleActive(lookbook)"
                :class="[
                  'flex-1 inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md transition-colors',
                  lookbook.actif 
                    ? 'bg-red-600 hover:bg-red-700 text-white' 
                    : 'bg-green-600 hover:bg-green-700 text-white'
                ]"
                :title="lookbook.actif ? 'Désactiver' : 'Activer'"
              >
                <i :class="lookbook.actif ? 'fas fa-pause' : 'fas fa-play'" class="mr-1"></i>
                {{ lookbook.actif ? 'Désactiver' : 'Activer' }}
              </button>
            </div>
            
            <!-- Actions secondaires -->
            <div class="flex items-center gap-2 mt-2">
              <!-- Bouton Aperçu -->
              <button 
                @click.stop="handlePreview(lookbook)"
                class="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
              >
                <i class="fas fa-eye mr-1"></i>
                Aperçu
              </button>
              
              <!-- Bouton Supprimer -->
              <button 
                @click.stop="handleDelete(lookbook)"
                class="inline-flex items-center justify-center w-10 h-10 border border-red-300 rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                title="Supprimer"
              >
                <i class="fas fa-trash text-sm"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Point d'ancrage pour le scroll (avec marge pour la navbar) -->
    <div id="scroll-anchor" class="h-0 -mt-20 pt-20"></div>
    
    <!-- Section Formulaire (cachée par défaut) -->
    <div 
      ref="formSection"
      v-show="showForm || editingLookbook"
      class="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mt-8"
    >
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-2xl font-bold text-gray-900">
          {{ editingLookbook ? 'Modifier le Lookbook' : 'Nouveau Lookbook' }}
        </h2>
        <button 
          @click="hideForm"
          class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <i class="fas fa-times mr-2"></i>
          Fermer
        </button>
      </div>
      
         <LookbookForm 
           :initial-data="editingLookbook ? {
             id: editingLookbook.id,
             titre: editingLookbook.titre,
             description: editingLookbook.description,
             collection: editingLookbook.collection,
             annee: editingLookbook.annee,
             couleurCadre: editingLookbook.couleurCadre,
             couleurTexte: editingLookbook.couleurTexte,
             categories: editingLookbook.categories,
             banniere: editingLookbook.banniere
           } : undefined"
           @submit="handleSubmitForm"
         />
    </div>
    
    <!-- Modal de confirmation de suppression -->
    <ModalComp
      :isVisible="showDeleteModal"
      title="Confirmer la suppression"
      size="md"
      @update:isVisible="showDeleteModal = false"
    >
      <template #body>
        <div class="text-center py-4">
          <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <i class="fas fa-exclamation-triangle text-red-600 text-xl"></i>
          </div>
          <h3 class="text-lg font-medium text-gray-900 mb-2">
            Supprimer le lookbook
          </h3>
          <p class="text-sm text-gray-500 mb-4">
            Êtes-vous sûr de vouloir supprimer le lookbook 
            <strong>"{{ lookbookToDelete?.titre.francais }}"</strong> ?
          </p>
          <p class="text-xs text-gray-400">
            Cette action est irréversible.
          </p>
        </div>
      </template>
      
      <template #footer>
        <div class="flex justify-end gap-3">
          <button
            @click="showDeleteModal = false"
            type="button"
            class="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none transition-colors"
          >
            Annuler
          </button>
          <button
            @click="confirmDelete"
            type="button"
            class="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none transition-colors"
          >
            <i class="fas fa-trash mr-2"></i>
            Supprimer
          </button>
        </div>
      </template>
    </ModalComp>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { PageHeader, LookbookForm, ModalComp } from '@/components'
import { getLookbooksByVendeur, getLookbookCategories } from '@/api'
import { useAuthStore } from '@/stores/auth'
import { useNotification } from '@/composables/useNotification'
import type { LookbookDisplay, LookbookFormData } from '@/types/lookbook'
import {useRouter} from "vue-router";

// Stores
const authStore = useAuthStore()
const router = useRouter()
// Notifications
const { success, error } = useNotification()

// État global
const lookbooks = ref<LookbookDisplay[]>([])
const showForm = ref(false)
const editingLookbook = ref<LookbookDisplay | null>(null)
const isLoading = ref(false)
const formSection = ref<HTMLDivElement>()

// État de la modal de suppression
const showDeleteModal = ref(false)
const lookbookToDelete = ref<LookbookDisplay | null>(null)

// Méthodes
const loadLookbooks = async () => {
  try {
    isLoading.value = true
    const idVendeur = authStore.user?.id_vendeur || authStore.user?.id
    
    if (!idVendeur) {
      error('ID vendeur introuvable')
      return
    }
    
    const lookbooksData = await getLookbooksByVendeur(idVendeur)
    
    // Charger les catégories pour chaque lookbook
    const lookbooksWithCategories = await Promise.all(
      lookbooksData.map(async (lookbook) => {
        try {
          const categories = await getLookbookCategories(lookbook.id)
          return {
            ...lookbook,
            categories: categories.map(cat => cat.id_categorie)
          }
        } catch (err) {
          return lookbook
        }
      })
    )
    
    lookbooks.value = lookbooksWithCategories
  } catch (err) {
    error('Erreur lors du chargement des lookbooks')
    lookbooks.value = []
  } finally {
    isLoading.value = false
  }
}

const showFormSection = () => {
  editingLookbook.value = null // Réinitialiser pour avoir un formulaire vierge
  showForm.value = true
  // Scroll vers le formulaire
  setTimeout(() => {
    formSection.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, 100)
}

const hideForm = () => {
  showForm.value = false
  editingLookbook.value = null
}

const handleEdit = (lookbook: LookbookDisplay) => {
  editingLookbook.value = lookbook
  // Scroll vers le point d'ancrage (fin de la liste des lookbooks)
  setTimeout(() => {
    const scrollAnchor = document.getElementById('scroll-anchor')
    if (scrollAnchor) {
      scrollAnchor.scrollIntoView({ behavior: 'smooth', block: 'start' })
    } else {
      // Fallback vers le formulaire si le point d'ancrage n'est pas trouvé
      formSection.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, 100)
}

const handleToggleActive = async (lookbook: LookbookDisplay) => {
  try {
    const { toggleLookbookActif } = await import('@/api')
    await toggleLookbookActif(lookbook.id)
    success(lookbook.actif ? 'Lookbook désactivé avec succès' : 'Lookbook activé avec succès')
    // Recharger les lookbooks pour obtenir l'état à jour
    await loadLookbooks()
  } catch (err) {
    error('Erreur lors de la modification du statut')
  }
}

const handleDelete = (lookbook: LookbookDisplay) => {
  lookbookToDelete.value = lookbook
  showDeleteModal.value = true
}

const confirmDelete = async () => {
  if (!lookbookToDelete.value) return
  
  try {
    const { deleteLookbook } = await import('@/api')
    await deleteLookbook(lookbookToDelete.value.id)
    success('Lookbook supprimé avec succès')
    showDeleteModal.value = false
    lookbookToDelete.value = null
    await loadLookbooks()
  } catch (err) {
    error('Erreur lors de la suppression du lookbook')
  }
}

const handlePreview = (lookbook: LookbookDisplay) => {
  const url = `https://www.efashion-paris.com/vendeurs-lookbook/&idModLookbook=${lookbook.id}`
  window.open(url, '_blank')
}

const handleSubmitForm = async (formData: LookbookFormData) => {
  try {
    const idVendeur = authStore.user?.id_vendeur || authStore.user?.id
    
    if (!idVendeur) {
      error('ID vendeur introuvable')
      return
    }
    
    if (editingLookbook.value) {
      // Modification d'un lookbook existant
      const { updateLookbook, uploadLookbookBanniere, saveLookbookCategories } = await import('@/api')
      
      await updateLookbook({
        id_mod_lookbook: editingLookbook.value.id,
        annee: formData.annee,
        id_collection: formData.collection,
        data: JSON.stringify({
          couleur_cadre: formData.couleurCadre,
          couleur_texte: formData.couleurTexte,
        }),
        titre_fr: formData.titre.francais,
        titre_en: formData.titre.anglais,
        titre_es: formData.titre.espagnol,
        titre_it: formData.titre.italien,
        description_fr: formData.description.francais,
        description_en: formData.description.anglais,
        description_es: formData.description.espagnol,
        description_it: formData.description.italien,
      })
      
      // Upload de la bannière si présente
      if (formData.banniere) {
        await uploadLookbookBanniere(editingLookbook.value.id, formData.banniere)
      }
      
      // Sauvegarder les catégories
      if (formData.categories && formData.categories.length > 0) {
        await saveLookbookCategories(editingLookbook.value.id, formData.categories)
      }
      
      success('Lookbook modifié avec succès')
    } else {
      // Création d'un nouveau lookbook
      const { createLookbook, uploadLookbookBanniere, saveLookbookCategories } = await import('@/api')
      
      const newLookbook = await createLookbook({
        id_vendeur: idVendeur,
        type: 0, // 0 = lookbook vendeur, 1 = edito Efashion
        annee: formData.annee,
        id_collection: formData.collection,
        data: JSON.stringify({
          couleur_cadre: formData.couleurCadre,
          couleur_texte: formData.couleurTexte,
        }),
        titre_fr: formData.titre.francais,
        titre_en: formData.titre.anglais,
        titre_es: formData.titre.espagnol,
        titre_it: formData.titre.italien,
        description_fr: formData.description.francais,
        description_en: formData.description.anglais,
        description_es: formData.description.espagnol,
        description_it: formData.description.italien,
        actif: false,
      })
      
      // Upload de la bannière si présente
      if (formData.banniere && newLookbook.id) {
        await uploadLookbookBanniere(newLookbook.id, formData.banniere)
      }
      
      // Sauvegarder les catégories
      if (formData.categories && formData.categories.length > 0 && newLookbook.id) {
        await saveLookbookCategories(newLookbook.id, formData.categories)
      }
      
      success('Lookbook créé avec succès')
    }
    
    // Recharger les lookbooks
    await loadLookbooks()
    
    // Fermer le formulaire et réinitialiser
    showForm.value = false
    editingLookbook.value = null
  } catch (err) {
    error('Erreur lors de la sauvegarde du lookbook')
  }
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
}

onMounted(async () => {
  if (!authStore.isInitialized) {
    await authStore.checkAuth()
  }

  if (!authStore.isAuthenticated) {
    router.push({ name: 'auth' })
    return
  }
  await loadLookbooks()
})
</script>

<style lang="scss" scoped></style>
